import { useState } from "react";
import { z } from "zod";
import {
  Grid,
  Link,
  MenuItem,
  Stack,
  Typography,
  Button,
  Alert,
  AlertProps,
} from "@mui/material";
import { Control, UseFormClearErrors, UseFormSetValue, useWatch } from "react-hook-form";

import {
  CodeStringNullableSchema,
  DateStringSchema,
  parseDateString,
  SafeStringForProse,
  toDateString,
} from "@packages/types";
import { sentenceCase } from "@packages/utils";

import { createAddIssue } from "~/utils";
import { IAdmissionTestType, IEnglishTestDetails } from "~/api";
import { useAdmissionTestTypes, useReferenceData } from "~/hooks/reference-data";
import { useGetEnglishTestDetails } from "~/hooks/english-test-verification";
import { ButtonLoadingSpinner } from "~/components/core/ButtonLoadingSpinner";
import { FormSection } from "~/components/form/FormSection";
import { FormRadioGroup } from "~/components/form/FormRadioGroup";
import { FormTextField } from "~/components/form/FormTextField";
import { FormSelect } from "~/components/form/FormSelect";

import { ApplicationFields } from "./useApplicationForm";

export enum EnglishProficiencyRequirement {
  TestPending = "TEST-TODO",
  TestDone = "TEST-DONE",
  WillNotComplete = "NO-TEST",
  OtherDocs = "OTHER-DOCS",
}

export const HAS_EXTERNAL_ASSESSMENT: string[] = [
  EnglishProficiencyRequirement.TestPending,
  EnglishProficiencyRequirement.TestDone,
];

// TODO: restore PEARSON-PTE when we get approval to release Pearson
// We will also need to update the "englishTestVerification" collection in Firestore.
// export const EXTERNAL_ASSESSMENT_PROVIDERS: string[] = ["IELTS", "TOEFL", "PEARSON-PTE"];
export const EXTERNAL_ASSESSMENT_PROVIDERS: string[] = ["IELTS", "TOEFL"];

const verifyTest = {
  "": () => false,
  Manual: () => false,
  IELTS: verifyTestUtil,
  TOEFL: verifyTestUtil,
  "PEARSON-PTE": verifyTestUtil,
};

export interface EnglishLanguageProficiencyFormProps {
  control: Control<ApplicationFields>;
  setValue: UseFormSetValue<ApplicationFields>;
  clearErrors: UseFormClearErrors<ApplicationFields>;
  disabled?: boolean;
}

export function EnglishLanguageProficiencyForm(
  props: EnglishLanguageProficiencyFormProps,
) {
  const { control, disabled, setValue, clearErrors } = props;

  const [
    crmEnglishTestId,
    externalEnglishTestSourceChannel,
    _externalEnglishTestId,
    externalEnglishTestScores,
    externalEnglishTestDateAchieved,
    firstName,
    lastName,
    dateOfBirth,
    previousFirstName,
    previousLastName,
    englishProficiencyRequirement,
    monashEnglishProgramPreference,
    englishTestScores,
  ] = useWatch({
    control,
    name: [
      "englishProficiency.englishTest.id",
      "englishProficiency.sourceChannel",
      "englishProficiency.testId",
      "englishProficiency.englishTest.scores",
      "englishProficiency.englishTest.dateAchieved",
      "personalDetails.legalGivenNames",
      "personalDetails.legalFamilyNames",
      "personalDetails.dateOfBirth",
      "monashStudies.previousFirstName",
      "monashStudies.previousLastName",
      "englishProficiency.englishProficiencyRequirement",
      "englishProficiency.monashEnglishProgramPreference",
      "englishProficiency.englishTest.scores",
    ],
  });

  const externalEnglishTestIdTrimmed = _externalEnglishTestId.trim();

  const allAdmissionTests = useAdmissionTestTypes();
  const hadEnglishInstructionOptions = useReferenceData("EnglishLanguageProficiency");
  const englishProficiencyLevelOptions = useReferenceData(
    "EnglishProficiencyRequirement",
  );

  // filter out LOI test and tests not available on Portal
  const admissionTests = allAdmissionTests.data?.filter(
    (test) => !isLOITest(test) && test.isAvailableOnPortal,
  );
  const loiTest = allAdmissionTests.data?.filter(isLOITest)[0]; // used for "The applicant has other documentation"

  const selectedTest = allAdmissionTests.data?.find((x) => x.id === crmEnglishTestId);

  const testVerificationAvailable =
    englishProficiencyRequirement === EnglishProficiencyRequirement.TestDone &&
    Boolean(selectedTest?.testVerificationDetails); // i.e. applicant has completed test AND test can be verified through integration
  const disableTestScoresManualEntry = externalEnglishTestSourceChannel !== "Manual";
  // Assume that if the source channel is not manual, a test ID is present, and there are scores,
  // then verification was done successfully, which means we can disable the date achieved field.
  const disableDateAchievedEntry =
    externalEnglishTestSourceChannel !== "Manual" &&
    externalEnglishTestIdTrimmed !== "" &&
    externalEnglishTestScores.some((score) => score.value);
  const [testVerificationStatus, setTestVerificationStatus] = useState<
    "default" | "verificationFailure" | "success"
  >("default");
  const englishTestDetails = useGetEnglishTestDetails(
    selectedTest?.testVerificationDetails?.testType,
    externalEnglishTestIdTrimmed,

    // only add startDate and endDate params if selected test type is TOEFL
    selectedTest?.testVerificationDetails?.testType === "TOEFL"
      ? `${new Date(externalEnglishTestDateAchieved).getFullYear()}-01-01`
      : undefined,
    selectedTest?.testVerificationDetails?.testType === "TOEFL"
      ? `${new Date(externalEnglishTestDateAchieved).getFullYear()}-12-31`
      : undefined,
  );

  function resetEnglishTest() {
    setValue("englishProficiency.englishTest", {
      id: "",
      dateAchieved: "",
      expectedDateOfCompletion: "",
      scores: [],
    });
    setValue("englishProficiency.otherDocumentation", "");
    setValue("englishProficiency.sourceChannel", "Manual");
  }

  function setEnglishTestScores(test?: IAdmissionTestType) {
    if (!test) {
      setValue("englishProficiency.englishTest.scores", []);
      return;
    }
    if (test.scores.length === 0)
      setValue("englishProficiency.englishTest.scores", [{ value: "" }]);
    else {
      const scoresArray: EnglishTestScore[] = test.scores.map((name) => ({
        name,
        value: "",
      }));
      // ensure that overall score is also added by appending to the end of the array
      scoresArray.push({ value: "" });
      setValue("englishProficiency.englishTest.scores", scoresArray);
    }
  }

  function setLOITest() {
    setValue("englishProficiency.englishTest", {
      id: loiTest?.id ?? "",
      dateAchieved: toDateString(new Date()),
      expectedDateOfCompletion: "",
      scores: [],
    });
    setValue("englishProficiency.otherDocumentation", "");
  }

  function resetEnglishTestVerification(test?: IAdmissionTestType) {
    setValue("englishProficiency.testId", "");
    setValue(
      "englishProficiency.sourceChannel",
      test?.testVerificationDetails?.testType ?? "Manual",
    );
    setTestVerificationStatus("default");
  }

  async function verifyEnglishTest() {
    // check if all the necessary data is available, and exit if not
    clearErrors("englishProficiency.testId");
    clearErrors("englishProficiency.englishTest.dateAchieved");
    clearErrors("englishProficiency.englishTest.scores");
    const selectedTestType = selectedTest?.testVerificationDetails?.testType;
    if (selectedTestType === "TOEFL" && !externalEnglishTestDateAchieved) {
      control.setError(
        "englishProficiency.englishTest.dateAchieved",
        {
          type: "custom",
          message: "Please enter the date the test was taken.",
        },
        { shouldFocus: true },
      );
      return;
    }

    if (testVerificationAvailable)
      setValue("englishProficiency.sourceChannel", selectedTestType ?? "Manual");
    const { data, isError } = await englishTestDetails.refetch();
    if (isError) {
      setTestVerificationStatus("verificationFailure");
      setEnglishTestScores(selectedTest);
      setValue("englishProficiency.sourceChannel", "Manual");
    }
    if (data) {
      // verify test details before updating scores
      if (
        verifyTest[selectedTest?.testVerificationDetails?.testType ?? "Manual"](data, {
          externalEnglishTestId: externalEnglishTestIdTrimmed,
          externalApiTestType:
            selectedTest?.testVerificationDetails?.externalApiTestType ?? "",
          dateOfBirth,
          firstName,
          previousFirstName,
          lastName,
          previousLastName,
        })
      ) {
        const {
          listeningScore,
          readingScore,
          speakingScore,
          writingScore,
          overallScore,
          testDate,
        } = data;
        setValue("englishProficiency.englishTest.scores", [
          {
            name: "Listening",
            value: String(listeningScore),
          },
          {
            name: "Reading",
            value: String(readingScore),
          },
          {
            name: "Speaking",
            value: String(speakingScore),
          },
          {
            name: "Writing",
            value: String(writingScore),
          },
          { value: String(overallScore) },
        ]);
        setValue("englishProficiency.englishTest.dateAchieved", testDate);
        setTestVerificationStatus("success");
      } else {
        setTestVerificationStatus("verificationFailure");
        setEnglishTestScores(selectedTest);
        setValue("englishProficiency.sourceChannel", "Manual");
      }
    }
  }

  return (
    <>
      <FormSection.Well>
        <Typography variant="body1" paragraph>
          To study at Monash, it is essential to have a strong command of English.
          Applicants must meet the{" "}
          <Link
            href="https://www.monash.edu/admissions/entry-requirements/english-language"
            target="_blank"
            rel="noopener"
          >
            English language requirements
          </Link>{" "}
          (including time limitations) to enrol in their preferred course.
        </Typography>
        <Typography variant="body1">
          The information provided as part of this application will be used by Monash to
          assess whether the applicant meets the English language requirement.
        </Typography>
      </FormSection.Well>
      <Stack gap={2}>
        <FormRadioGroup
          control={control}
          name="englishProficiency.hadEnglishInstruction.code"
          disabled={disabled}
          label={
            <>
              Has the applicant completed at least six years of schooling in an English
              medium institution in an{" "}
              <Link
                href="https://www.monash.edu/admissions/entry-requirements/english-language"
                target="_blank"
                rel="noopener"
              >
                English-speaking country
              </Link>{" "}
              prior to the age of 19?
            </>
          }
          loading={hadEnglishInstructionOptions.isPending}
          options={hadEnglishInstructionOptions.data}
        />
        <FormSelect
          control={control}
          name="englishProficiency.englishProficiencyRequirement"
          label="Please select the statement that most closely describes the applicant's level of English proficiency"
          loading={englishProficiencyLevelOptions.isPending}
          disabled={disabled}
          onBeforeChange={(e) => {
            const selectedOption = e.target.value;
            if (selectedOption === EnglishProficiencyRequirement.OtherDocs) {
              setLOITest();
            } else {
              resetEnglishTest();
              setEnglishTestScores(
                selectedOption === EnglishProficiencyRequirement.TestDone
                  ? selectedTest
                  : undefined,
              );
            }
            resetEnglishTestVerification();
          }}
        >
          {englishProficiencyLevelOptions.data?.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </FormSelect>
      </Stack>
      <Grid container spacing={2}>
        {HAS_EXTERNAL_ASSESSMENT.includes(englishProficiencyRequirement) && (
          <>
            <Grid item xs={12}>
              <FormSelect
                control={control}
                name="englishProficiency.englishTest.id"
                label="Test Type"
                loading={allAdmissionTests.isPending}
                disabled={disabled}
                onBeforeChange={(e) => {
                  const id = e.target.value;
                  const newTest = admissionTests?.find((x) => x.id === id);
                  const haveSatEnglishTest =
                    englishProficiencyRequirement ===
                    EnglishProficiencyRequirement.TestDone;

                  // Unset automated english test verification values and return to default
                  setValue("englishProficiency.testId", "");
                  setValue(
                    "englishProficiency.sourceChannel",
                    haveSatEnglishTest
                      ? (newTest?.testVerificationDetails?.testType ?? "Manual")
                      : "Manual",
                  );
                  setTestVerificationStatus("default");

                  // Don't need to set scores if the test has not been taken
                  if (!haveSatEnglishTest) return;
                  setEnglishTestScores(newTest);
                }}
              >
                {admissionTests?.map(({ id, description }) => (
                  <MenuItem key={id} value={id}>
                    {description}
                  </MenuItem>
                ))}
              </FormSelect>
            </Grid>
            {testVerificationAvailable && (
              <Grid item xs={12}>
                <Alert
                  severity={getTestVerificationAlertSeverity(testVerificationStatus)}
                >
                  {
                    TEST_VERIFICATION_MESSAGES(
                      selectedTest?.testVerificationDetails?.testType,
                    )[testVerificationStatus]
                  }
                </Alert>
              </Grid>
            )}
            {englishProficiencyRequirement === EnglishProficiencyRequirement.TestDone && (
              <Grid item xs={12} sm={6}>
                <FormTextField
                  control={control}
                  name="englishProficiency.englishTest.dateAchieved"
                  label="Date achieved"
                  disabled={disabled || disableDateAchievedEntry}
                  type="date"
                  fullWidth
                />
              </Grid>
            )}
            {englishProficiencyRequirement ===
              EnglishProficiencyRequirement.TestPending && (
              <Grid item xs={12} sm={6}>
                <FormTextField
                  control={control}
                  name="englishProficiency.englishTest.expectedDateOfCompletion"
                  label="Expected completion date"
                  disabled={disabled}
                  type="date"
                  fullWidth
                />
              </Grid>
            )}
            {!testVerificationAvailable && (
              <Grid item display={{ xs: "none", sm: "block" }} sm={6} xs={0} />
            )}
            {testVerificationAvailable && (
              <Grid item xs={12} sm={6}>
                <FormTextField
                  control={control}
                  name={`englishProficiency.testId`}
                  label={
                    TEST_ID_LABEL[
                      selectedTest?.testVerificationDetails?.testType ?? "default"
                    ]
                  }
                  disabled={disabled}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <Button
                        variant="contained"
                        sx={{ px: englishTestDetails.isFetching ? 4.5 : 3.5 }}
                        disabled={
                          !externalEnglishTestIdTrimmed ||
                          englishTestDetails.isFetching ||
                          disabled
                        }
                        onClick={verifyEnglishTest}
                        endIcon={
                          <ButtonLoadingSpinner show={englishTestDetails.isFetching} />
                        }
                      >
                        Verify&nbsp;Test
                      </Button>
                    ),
                  }}
                />
              </Grid>
            )}
            {englishProficiencyRequirement === EnglishProficiencyRequirement.TestDone &&
              englishTestScores.map(({ name }, index) => (
                <Grid item key={name ?? index} xs={6}>
                  <FormTextField
                    control={control}
                    type="number"
                    name={`englishProficiency.englishTest.scores.${index}.value`}
                    // If the "name" property is empty, we assume it is the overall score
                    label={name ? `${sentenceCase(name)} score` : "Overall score"}
                    disabled={disabled || disableTestScoresManualEntry}
                    fullWidth
                  />
                </Grid>
              ))}
          </>
        )}
        {englishProficiencyRequirement === EnglishProficiencyRequirement.OtherDocs && (
          <Grid item xs={12}>
            <FormTextField
              control={control}
              name="englishProficiency.otherDocumentation"
              label="Enter details for other documentation that satisfies English proficiency"
              multiline
              minRows={3}
              maxRows={12}
              disabled={disabled}
              fullWidth
            />
          </Grid>
        )}
        {monashEnglishProgramPreference && (
          <>
            <Grid item xs={12} mt={2}>
              <FormRadioGroup
                control={control}
                name="englishProficiency.monashEnglishProgramPreference"
                disabled={disabled}
                label="If the applicant's English test result does not meet the minimum English language requirement for their preferred course, would they like to be considered for a Monash English program?"
                options={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                The applicant has previously indicated their response to the above
                question. You may modify their response, subject to the applicant's
                approval.
              </Alert>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}

/** Utility function to check if a given admission test is the LOI (Language of Instruction) Test. */
function isLOITest(test: IAdmissionTestType) {
  return test.description === "LOI (Language of Instruction)";
}

export type EnglishTestScore = z.infer<typeof EnglishTestScoreSchema>;
export const EnglishTestScoreSchema = z.object({
  name: z.string().optional(),
  value: z.preprocess(
    (value) => {
      if (value === "") return "";
      return Number(value);
    },
    z
      .number({ invalid_type_error: "Please provide a valid number" })
      .min(0, "Cannot be less than 0")
      .max(999, "Cannot be greater than 999")
      .transform((score) => score.toString())
      .or(z.literal("")),
  ),
});

export const EnglishTestSchema = z.object({
  recordId: z.string().optional(),
  id: z.string(),
  dateAchieved: DateStringSchema.allowBlank(),
  expectedDateOfCompletion: DateStringSchema.allowBlank(),
  scores: z.array(EnglishTestScoreSchema),
});

EnglishLanguageProficiencyForm.draftSchema = z
  .object({
    hadEnglishInstruction: CodeStringNullableSchema,
    englishTest: EnglishTestSchema,
    otherDocumentation: SafeStringForProse.schema(),
    // Note: MIX only accepts the value "PEARSON" rather than "PEARSON-PTE",
    // however, "PEARSON-PTE" is used to avoid having to maintain two sets of types between testType and sourceChannel.
    // This value is modified in the backend when updating/fetching the application
    // (see application-details.resolver.ts)
    sourceChannel: z.enum(["Manual", "IELTS", "TOEFL", "PEARSON-PTE", ""]),
    testId: z.string().trim(),
    englishProficiencyRequirement: z.enum([
      "TEST-TODO",
      "TEST-DONE",
      "NO-TEST",
      "OTHER-DOCS",
      "",
    ]),
    monashEnglishProgramPreference: z.enum(["Yes", "No", ""]),
  })
  .superRefine(({ englishProficiencyRequirement, englishTest, sourceChannel }, ctx) => {
    const addIssue = createAddIssue(ctx);

    if (HAS_EXTERNAL_ASSESSMENT.includes(englishProficiencyRequirement)) {
      if (!englishTest.id) addIssue("Please select a test type", "englishTest.id");

      if (englishProficiencyRequirement === EnglishProficiencyRequirement.TestDone) {
        if (!englishTest.dateAchieved)
          addIssue(
            "Please provide the date the test score was achieved",
            "englishTest.dateAchieved",
          );
        else {
          const date = parseDateString(englishTest.dateAchieved);
          if (date.getTime() > Date.now())
            addIssue("Date achieved must be in the past", "englishTest.conferredDate");
        }

        if (EXTERNAL_ASSESSMENT_PROVIDERS.includes(sourceChannel)) {
          if (englishTest.scores.some((score) => !score.value))
            addIssue("Please verify the applicant's English test", "testId");
        } else {
          englishTest.scores.forEach((score, index) => {
            if (!score.value)
              addIssue("Please provide a score", `englishTest.scores.${index}.value`);
          });
        }
      }

      if (englishProficiencyRequirement === EnglishProficiencyRequirement.TestPending) {
        if (!englishTest.expectedDateOfCompletion)
          addIssue(
            "Please provide the expected completion date",
            "englishTest.expectedDateOfCompletion",
          );
        else {
          const date = parseDateString(englishTest.expectedDateOfCompletion);
          if (date.getTime() < Date.now())
            addIssue(
              "Expected date of completion must be in the future",
              "englishTest.expectedDateOfCompletion",
            );
        }
      }
    }
  });

EnglishLanguageProficiencyForm.submitSchema =
  EnglishLanguageProficiencyForm.draftSchema.superRefine(
    (
      {
        hadEnglishInstruction,
        englishProficiencyRequirement,
        otherDocumentation,
        sourceChannel,
        testId,
      },
      ctx,
    ) => {
      const addIssue = createAddIssue(ctx);

      if (!hadEnglishInstruction?.code)
        addIssue("Please select an answer", "hadEnglishInstruction.code");

      if (!englishProficiencyRequirement)
        addIssue("Please select one of the available options", "englishTest");

      if (
        englishProficiencyRequirement === EnglishProficiencyRequirement.OtherDocs &&
        !otherDocumentation
      )
        addIssue("Please provide documentation", "otherDocumentation");

      if (
        englishProficiencyRequirement === EnglishProficiencyRequirement.TestDone &&
        EXTERNAL_ASSESSMENT_PROVIDERS.includes(sourceChannel) &&
        !testId
      )
        addIssue("Please verify the applicant's English test", "testId");
    },
  );

const TEST_ID_LABEL = {
  default: "Test Number",
  IELTS: "Test Report Form Number",
  TOEFL: "Appointment Number",
  "PEARSON-PTE": "Score Report Code",
};

const TEST_VERIFICATION_MESSAGES = (testType: string = "") => ({
  default: (
    <>
      For this test type, please enter the test number and click the VERIFY TEST button to
      verify the applicant's English test results with the official provider.{" "}
      {testType === "PEARSON-PTE" ? (
        <>
          If successfully verified the applicant's English test results will be uploaded
          as a PDF in the Documents section.
        </>
      ) : (
        <>
          Before verifying the test, please ensure that the applicant has authorized
          Monash University as a score recipient in their {testType} account via the
          booking portal.
        </>
      )}
    </>
  ),
  verificationFailure: (
    <>
      English test verification unsuccessful. Check the information you have entered below
      and try again or manually enter the applicant's test scores below. Remember to also
      upload the applicant's English test result in the Supporting Documents section.
    </>
  ),
  success:
    testType === "PEARSON-PTE" ? (
      <>English test verification successful.</>
    ) : (
      <>
        English test verification successful. Remember to also upload the applicant's
        English test results in the Supporting Documents section.
      </>
    ),
});

function getTestVerificationAlertSeverity(
  status: "default" | "verificationFailure" | "success",
): AlertProps["severity"] {
  switch (status) {
    case "success":
      return "success";
    case "verificationFailure":
      return "error";
    default:
      return "info";
  }
}
/**
 * Util method for verifying all tests.
 */
export function verifyTestUtil(
  data: IEnglishTestDetails,
  testArgs: {
    externalEnglishTestId: string;
    externalApiTestType: string | string[];
    dateOfBirth: string;
    firstName: string;
    previousFirstName: string;
    lastName: string;
    previousLastName: string;
  },
) {
  const {
    externalEnglishTestId,
    externalApiTestType,
    dateOfBirth,
    firstName,
    lastName,
    previousFirstName,
    previousLastName,
  } = testArgs;

  // externalApiTestType may be a single value or an array. We need to coerce this to an array with at least one element.
  const externalApiTestTypesArray = Array.isArray(externalApiTestType)
    ? [...externalApiTestType]
    : [externalApiTestType];

  return (
    externalEnglishTestId === data.testNumber &&
    externalApiTestTypesArray.some((matchString) =>
      data.testType.includes(matchString),
    ) &&
    dateOfBirth === data.dateOfBirth &&
    (firstName.toLowerCase() === data.firstName.toLowerCase() ||
      previousFirstName.toLowerCase() === data.firstName.toLowerCase()) &&
    (lastName.toLowerCase() === data.lastName.toLowerCase() ||
      previousLastName.toLowerCase() === data.lastName.toLowerCase())
  );
}
